import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStatus from "../../hooks/useAuthStatus";
import { getTransactionHistory } from "../../api/apiServices";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

const filters = ["All", "Buy", "Sell", "Deposit", "Withdraw"];

const typeLabel = { 
  buy: "Bought", 
  sell: "Sold", 
  deposit: "Deposited", 
  withdraw_crypto: "Withdrew Crypto", 
  withdraw_naira: "Withdrew NGN" 
};

const typeIcon = { 
  buy: "ri-arrow-down-line", 
  sell: "ri-arrow-up-line", 
  deposit: "ri-arrow-down-circle-line", 
  withdraw_crypto: "ri-arrow-up-circle-line", 
  withdraw_naira: "ri-bank-line" 
};

const typeColor = { 
  buy: "#059669", 
  sell: "#DC2626", 
  deposit: "#059669", 
  withdraw_crypto: "#D97706", 
  withdraw_naira: "#D97706" 
};

const typeBg = { 
  buy: "#ECFDF5", 
  sell: "#FEF2F2", 
  deposit: "#ECFDF5", 
  withdraw_crypto: "#FFFBEB", 
  withdraw_naira: "#FFFBEB" 
};

export default function Transactions() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Fetch Transactions
  const fetchTransactions = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await getTransactionHistory();
      
      if (res.status === true) {
        setTransactions(res.data);
        setError(null);
      } else {
        setError("Failed to load transactions");
      }
    } catch (err) {
      console.error(err);
      setError("Unable to fetch transaction history");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [isAuthenticated]);

  // Pull to Refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTransactions();
  };

  // Filtered & Searched Transactions
  const filteredTransactions = useMemo(() => {
    let result = transactions;

    // Filter by type
    if (activeFilter !== "All") {
      result = result.filter(tx => {
        if (activeFilter === "Buy") return tx.type === "buy";
        if (activeFilter === "Sell") return tx.type === "sell";
        if (activeFilter === "Deposit") return tx.type === "deposit";
        if (activeFilter === "Withdraw") return tx.type === "withdraw_crypto" || tx.type === "withdraw_naira";
        return true;
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(tx => 
        tx.asset?.toLowerCase().includes(q) ||
        tx.paystackReference?.toLowerCase().includes(q) ||
        tx.accountNumber?.toLowerCase().includes(q) ||
        tx.bankName?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [transactions, activeFilter, searchQuery]);

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-4 flex items-center gap-3 border-b border-slate-100">
        <button onClick={() => navigate("/home")} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200">
          <i className="ri-arrow-left-line text-slate-600 text-lg"></i>
        </button>
        <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Transaction History
        </h1>

        {!isAuthenticated && (
          <button onClick={() => navigate("/auth")} className="ml-auto px-5 py-2 text-sm font-semibold text-emerald-600 border border-emerald-200 rounded-xl hover:bg-emerald-50 transition-colors">
            Sign In
          </button>
        )}
      </div>

      {/* Search Bar */}
      {isAuthenticated && (
        <div className="px-5 pt-4 pb-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by asset, reference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-4 pl-11 text-sm focus:outline-none focus:border-emerald-500"
            />
            <i className="ri-search-line absolute left-4 top-3.5 text-slate-400 text-lg"></i>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 px-5 py-3 overflow-x-auto">
        {filters.map(f => (
          <button 
            key={f} 
            onClick={() => setActiveFilter(f)}
            disabled={!isAuthenticated}
            className={`flex-shrink-0 px-5 py-2 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ 
              background: activeFilter === f && isAuthenticated ? "#059669" : "#fff", 
              color: activeFilter === f && isAuthenticated ? "#fff" : "#94A3B8", 
              border: activeFilter === f && isAuthenticated ? "none" : "1px solid #E2E8F0" 
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Transactions List with Pull to Refresh */}
      <div 
        className="px-5 space-y-3"
        onTouchStart={(e) => {
          if (e.touches[0].clientY < 100 && isAuthenticated) handleRefresh();
        }}
      >
        {!isAuthenticated ? (
          <div className="min-h-[400px] flex items-center justify-center bg-white/95 backdrop-blur-md rounded-3xl border border-slate-200">
            <div className="text-center px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
                <i className="ri-lock-2-line text-slate-400 text-5xl"></i>
              </div>
              <p className="font-semibold text-slate-700 text-lg mb-1">Login to view transactions</p>
              <button onClick={() => navigate("/auth")} className="px-8 py-3 bg-emerald-600 text-white font-semibold rounded-2xl text-sm mt-4">
                Sign in to Unlock
              </button>
            </div>
          </div>
        ) : loading && !refreshing ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-600">
            <p>{error}</p>
            <button onClick={fetchTransactions} className="mt-4 text-emerald-600 font-medium">Retry</button>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-16">
            <i className="ri-file-list-3-line text-slate-300 text-6xl mb-3 block"></i>
            <p className="text-slate-400">No transactions found</p>
          </div>
        ) : (
          filteredTransactions.map(tx => (
            <div 
            key={tx._id} 
            onClick={() => navigate(`/transaction/${tx._id}`)}
            className="bg-white border border-slate-100 rounded-2xl px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" 
                       style={{ background: typeBg[tx.type] }}>
                    <i className={`${typeIcon[tx.type]} text-lg`} style={{ color: typeColor[tx.type] }}></i>
                  </div>
                  <div>
                    <p className="text-slate-800 text-sm font-bold">
                      {typeLabel[tx.type]} {tx.asset !== "NGN" ? tx.asset : ""}
                    </p>
                    <p className="text-slate-400 text-xs">
                      {new Date(tx.createdAt).toLocaleDateString("en-NG", { 
                        day: "numeric", month: "short", year: "numeric" 
                      })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-slate-800 text-sm font-bold">
                    {tx.type === "sell" || tx.type === "withdraw_naira" ? "-" : ""}₦{tx.nairaAmount?.toLocaleString() || 0}
                  </p>
                  {tx.cryptoAmount > 0 && (
                    <p className="text-slate-400 text-xs">
                      {tx.type === "sell" || tx.type === "withdraw_crypto" ? "-" : "+"}
                      {tx.cryptoAmount} {tx.asset}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-50">
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  tx.status === "completed" ? "bg-emerald-50 text-emerald-600" : 
                  tx.status === "pending" || tx.status === "processing" ? "bg-amber-50 text-amber-600" : 
                  "bg-red-50 text-red-600"
                }`}>
                  {tx.status}
                </span>

                {tx.paystackReference && (
                  <span className="text-slate-300 text-xs font-mono">
                    {tx.paystackReference.slice(0, 12)}...
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
}